import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import {
  FaChartLine,
  FaApple,
  FaGooglePlay,
  FaUsers,
  FaSignOutAlt,
  FaSync,
  FaExclamationTriangle,
  FaGamepad,
  FaSearch,
  FaTimes,
  FaEye,
  FaStopCircle,
  FaBell,
  FaTrash,
  FaRobot,
  FaCalendarDay,
  FaCheckCircle,
  FaPercentage,
  FaCog,
  FaSave,
  FaMobileAlt
} from 'react-icons/fa';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell
} from 'recharts';

// ============================================================================
// Types
// ============================================================================

type TabType = 'overview' | 'games' | 'users' | 'settings';

interface AdminGameSummary {
  id: string;
  status: string;
  mode: string;
  players: { id: string; name: string; isBot: boolean; score: number }[];
  currentRound: number;
  createdAt: string;
  lastActivityAt: string;
  winner: string | null;
}

interface GameDetail extends AdminGameSummary {
  deck?: any[];
  tableCards?: any[];
  scores?: Record<string, any>;
  hostId?: string;
  inviteCode?: string | null;
  _debug?: {
    deckCount: number;
    deck: any[];
    playerHands: { playerId: string; hand: any[] }[];
  };
}

interface AdminUserSummary {
  id: string;
  displayName: string | null;
  email?: string;
  stats?: { gamesPlayed: number; gamesWon: number; rating: number };
  activeGamesCount: number;
  lastActiveAt?: string;
}

interface UserDetail extends AdminUserSummary {
  photoURL?: string;
  emailVerified?: boolean;
  disabled?: boolean;
  createdAt?: string;
  lastSignInAt?: string;
  recentGames?: AdminGameSummary[];
  pushTokens?: string[];
  pushTokenCount?: number;
  subscription?: any;
}

interface DailyRevenueItem {
  date: string;
  revenue: number;
  currency: string;
  transactions: number;
}

interface RevenueData {
  combined: {
    totalRevenue: number;
    currency: string;
    periodStart: string;
    periodEnd: string;
  };
  googlePlay: {
    platform: string;
    totalRevenue: number;
    dailyRevenue?: DailyRevenueItem[];
    error?: string;
  };
  appStore: {
    platform: string;
    totalRevenue: number;
    dailyRevenue?: DailyRevenueItem[];
    error?: string;
  };
}

interface StatsData {
  totalUsers: number;
  recentGames: number;
  activeGames: number;
  gamesToday: number;
  botOnlyAllTime: number;
  botOnlyToday: number;
  gamesByStatus: {
    completed: number;
    abandoned: number;
    active: number;
    waiting: number;
  };
  gamesByMode: {
    LOCAL: number;
    ASYNC: number;
    LIVE: number;
  };
  completionRate: number;
  userGrowth: { month: string; count: number; cumulative: number }[];
}

// ============================================================================
// Styled Components
// ============================================================================

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0a1628 0%, #0f1f3a 100%);
  color: white;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const Logo = styled.h1`
  font-family: 'Outfit', sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-gold, #d4a574);
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 107, 107, 0.2);
  border: 1px solid rgba(255, 107, 107, 0.3);
  border-radius: 8px;
  color: #ff6b6b;
  font-family: 'Plus Jakarta Sans', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 107, 107, 0.3);
  }
`;

const TabNav = styled.nav`
  display: flex;
  gap: 0.5rem;
  padding: 1rem 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const TabButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: ${props => props.$active ? 'rgba(212, 165, 116, 0.2)' : 'transparent'};
  border: 1px solid ${props => props.$active ? 'rgba(212, 165, 116, 0.5)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 8px;
  color: ${props => props.$active ? '#d4a574' : 'rgba(255, 255, 255, 0.7)'};
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$active ? 'rgba(212, 165, 116, 0.2)' : 'rgba(255, 255, 255, 0.05)'};
    color: ${props => props.$active ? '#d4a574' : 'white'};
  }
`;

const Main = styled.main`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
`;

const Section = styled.section`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-family: 'Outfit', sans-serif;
  font-size: 1.25rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 1.5rem;
`;

const StatLabel = styled.div`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 0.5rem;
`;

const StatValue = styled.div`
  font-family: 'Outfit', sans-serif;
  font-size: 2rem;
  font-weight: 700;
  color: white;
`;

const RevenueGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const RevenueCard = styled.div<{ platform: 'combined' | 'google' | 'apple' }>`
  background: ${props => {
    switch (props.platform) {
      case 'combined': return 'linear-gradient(135deg, rgba(212, 165, 116, 0.15) 0%, rgba(212, 165, 116, 0.05) 100%)';
      case 'google': return 'linear-gradient(135deg, rgba(52, 168, 83, 0.15) 0%, rgba(52, 168, 83, 0.05) 100%)';
      case 'apple': return 'linear-gradient(135deg, rgba(0, 122, 255, 0.15) 0%, rgba(0, 122, 255, 0.05) 100%)';
    }
  }};
  border: 1px solid ${props => {
    switch (props.platform) {
      case 'combined': return 'rgba(212, 165, 116, 0.3)';
      case 'google': return 'rgba(52, 168, 83, 0.3)';
      case 'apple': return 'rgba(0, 122, 255, 0.3)';
    }
  }};
  border-radius: 20px;
  padding: 2rem;
`;

const RevenueHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const RevenueIcon = styled.div<{ platform: 'combined' | 'google' | 'apple' }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  background: ${props => {
    switch (props.platform) {
      case 'combined': return 'rgba(212, 165, 116, 0.2)';
      case 'google': return 'rgba(52, 168, 83, 0.2)';
      case 'apple': return 'rgba(0, 122, 255, 0.2)';
    }
  }};
  color: ${props => {
    switch (props.platform) {
      case 'combined': return '#d4a574';
      case 'google': return '#34a853';
      case 'apple': return '#007aff';
    }
  }};
`;

const RevenuePlatform = styled.div`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.8);
`;

const RevenueAmount = styled.div`
  font-family: 'Outfit', sans-serif;
  font-size: 2.5rem;
  font-weight: 700;
  color: white;
  margin-bottom: 0.5rem;
`;

const RevenuePeriod = styled.div`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.5);
`;

const ErrorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: rgba(255, 193, 7, 0.15);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 12px;
  padding: 1rem 1.25rem;
  margin-top: 1rem;
  color: #ffc107;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.9rem;
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: white;
  font-family: 'Plus Jakarta Sans', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: auto;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingSpinner = styled(FaSync)`
  animation: spin 1s linear infinite;

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ChartCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 1.5rem;
  margin-top: 1.5rem;
`;

const ChartTitle = styled.h3`
  font-family: 'Outfit', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 1rem;
`;

const NoDataMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: rgba(255, 255, 255, 0.5);
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

// Games & Users Section Styles
const FilterBar = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const FilterSelect = styled.select`
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: white;
  font-family: 'Plus Jakarta Sans', sans-serif;
  cursor: pointer;

  option {
    background: #0f1f3a;
    color: white;
  }
`;

const SearchInput = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  flex: 1;
  max-width: 400px;

  input {
    flex: 1;
    background: transparent;
    border: none;
    color: white;
    font-family: 'Plus Jakarta Sans', sans-serif;
    outline: none;

    &::placeholder {
      color: rgba(255, 255, 255, 0.4);
    }
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  overflow: hidden;
`;

const Th = styled.th`
  padding: 1rem;
  text-align: left;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  background: rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const Td = styled.td`
  padding: 1rem;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
`;

const Tr = styled.tr`
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-flex;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => {
    switch (props.status) {
      case 'WAITING': return 'rgba(255, 193, 7, 0.2)';
      case 'ACTIVE': return 'rgba(52, 168, 83, 0.2)';
      case 'PAUSED': return 'rgba(0, 122, 255, 0.2)';
      case 'COMPLETED': return 'rgba(156, 39, 176, 0.2)';
      case 'ABANDONED': return 'rgba(255, 107, 107, 0.2)';
      default: return 'rgba(255, 255, 255, 0.1)';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'WAITING': return '#ffc107';
      case 'ACTIVE': return '#34a853';
      case 'PAUSED': return '#007aff';
      case 'COMPLETED': return '#9c27b0';
      case 'ABANDONED': return '#ff6b6b';
      default: return 'rgba(255, 255, 255, 0.6)';
    }
  }};
`;

const LoadMoreButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 1rem;
  margin-top: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.7);
  font-family: 'Plus Jakarta Sans', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, #0a1628 0%, #0f1f3a 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  max-width: 800px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const ModalTitle = styled.h3`
  font-family: 'Outfit', sans-serif;
  font-size: 1.25rem;
  font-weight: 600;
  color: white;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    color: white;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.6);
`;

const DetailValue = styled.span`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.9rem;
  color: white;
`;

const ActionButton = styled.button<{ variant?: 'danger' | 'warning' | 'primary' }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: ${props => {
    switch (props.variant) {
      case 'danger': return 'rgba(255, 107, 107, 0.2)';
      case 'warning': return 'rgba(255, 193, 7, 0.2)';
      default: return 'rgba(212, 165, 116, 0.2)';
    }
  }};
  border: 1px solid ${props => {
    switch (props.variant) {
      case 'danger': return 'rgba(255, 107, 107, 0.3)';
      case 'warning': return 'rgba(255, 193, 7, 0.3)';
      default: return 'rgba(212, 165, 116, 0.3)';
    }
  }};
  border-radius: 8px;
  color: ${props => {
    switch (props.variant) {
      case 'danger': return '#ff6b6b';
      case 'warning': return '#ffc107';
      default: return '#d4a574';
    }
  }};
  font-family: 'Plus Jakarta Sans', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ActionRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const DebugSection = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
`;

const DebugTitle = styled.h4`
  font-family: 'Outfit', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0.75rem;
`;

const DebugCode = styled.pre`
  font-family: monospace;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  overflow-x: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const GameLink = styled.button`
  background: none;
  border: none;
  color: #d4a574;
  text-decoration: underline;
  cursor: pointer;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.9rem;

  &:hover {
    color: #e5b785;
  }
`;

// Card Visualization Components
const MiniCard = styled.div<{ $suit: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 48px;
  background: #fff;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  font-weight: bold;
  font-size: 0.85rem;
  color: ${props => ['HEARTS', 'DIAMONDS'].includes(props.$suit) ? '#dc2626' : '#1f2937'};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  flex-direction: column;
  line-height: 1;
`;

const HiddenCard = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 48px;
  background: linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%);
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.3);
  font-size: 1.2rem;
`;

const CardRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin: 8px 0;
`;

const RevealButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.75rem;
  background: rgba(212, 165, 116, 0.15);
  border: 1px solid rgba(212, 165, 116, 0.3);
  border-radius: 6px;
  color: #d4a574;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: auto;

  &:hover {
    background: rgba(212, 165, 116, 0.25);
  }
`;

const PlayerHandSection = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const PlayerHandLabel = styled.div`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0.5rem;
`;

// ============================================================================
// Main Component
// ============================================================================

const API_URL = import.meta.env.VITE_API_URL || '/api';

const AdminDashboardPage: React.FC = () => {
  const { token, signOut, isAdmin, loading: authLoading } = useAdminAuth();
  const navigate = useNavigate();

  // Tab State
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTabState] = useState<TabType>(() => {
    const tab = searchParams.get('tab');
    return (tab === 'overview' || tab === 'games' || tab === 'users' || tab === 'settings') ? tab : 'overview';
  });
  const setActiveTab = (tab: TabType) => {
    setActiveTabState(tab);
    setSearchParams({ tab });
  };

  // Overview State
  const [revenue, setRevenue] = useState<RevenueData | null>(null);
  const [stats, setStats] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Games State
  const [games, setGames] = useState<AdminGameSummary[]>([]);
  const [gamesLoading, setGamesLoading] = useState(false);
  const [gamesCursor, setGamesCursor] = useState<string | null>(null);
  const [gamesHasMore, setGamesHasMore] = useState(false);
  const [gameFilters, setGameFilters] = useState({ status: 'all', mode: 'all' });
  const [gameSearch, setGameSearch] = useState('');
  const [selectedGame, setSelectedGame] = useState<GameDetail | null>(null);
  const [gameDetailLoading, setGameDetailLoading] = useState(false);

  // Users State
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersCursor, setUsersCursor] = useState<string | null>(null);
  const [usersHasMore, setUsersHasMore] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserDetail | null>(null);
  const [userDetailLoading, setUserDetailLoading] = useState(false);

  // Card Visibility State
  const [showSensitiveCards, setShowSensitiveCards] = useState(false);

  // Settings State
  const [versionConfig, setVersionConfig] = useState({
    ios: { minVersion: '', latestVersion: '' },
    android: { minVersion: '', latestVersion: '' },
    updatedAt: null as string | null,
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Card Display Helpers
  const getSuitSymbol = (suit: string): string => {
    const symbols: Record<string, string> = {
      CLUBS: '\u2663',
      DIAMONDS: '\u2666',
      HEARTS: '\u2665',
      SPADES: '\u2660'
    };
    return symbols[suit] || '?';
  };

  const getRankDisplay = (rank: number): string => {
    const faces: Record<number, string> = { 1: 'A', 11: 'J', 12: 'Q', 13: 'K' };
    return faces[rank] || rank.toString();
  };

  const renderCard = (card: { suit: string; rank: number }) => (
    <MiniCard key={`${card.suit}-${card.rank}`} $suit={card.suit}>
      <span>{getRankDisplay(card.rank)}</span>
      <span>{getSuitSymbol(card.suit)}</span>
    </MiniCard>
  );

  const renderHiddenCard = (index: number) => (
    <HiddenCard key={index}>?</HiddenCard>
  );

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/admin');
    }
  }, [isAdmin, authLoading, navigate]);

  // Overview Data Fetch
  const fetchOverviewData = useCallback(async () => {
    try {
      if (!token) return;

      const headers = { 'Authorization': `Bearer ${token}` };

      const [revenueRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/admin/revenue`, { headers }),
        fetch(`${API_URL}/admin/stats`, { headers })
      ]);

      if (revenueRes.ok) {
        const revenueData = await revenueRes.json();
        setRevenue(revenueData.data);
      }

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData.data);
      }
    } catch (err) {
      console.error('Error fetching admin data:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => {
    if (token && isAdmin) {
      fetchOverviewData();
    }
  }, [token, isAdmin, fetchOverviewData]);

  // Games Fetch
  const fetchGames = useCallback(async (reset: boolean = true) => {
    if (!token) return;
    setGamesLoading(true);

    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const params = new URLSearchParams();

      if (gameFilters.status !== 'all') params.append('status', gameFilters.status);
      if (gameFilters.mode !== 'all') params.append('mode', gameFilters.mode);
      if (gameSearch) params.append('search', gameSearch);
      if (!reset && gamesCursor) params.append('cursor', gamesCursor);

      const res = await fetch(`${API_URL}/admin/games?${params}`, { headers });
      if (res.ok) {
        const data = await res.json();
        if (reset) {
          setGames(data.data.games);
        } else {
          setGames(prev => [...prev, ...data.data.games]);
        }
        setGamesCursor(data.data.nextCursor);
        setGamesHasMore(data.data.hasMore);
      }
    } catch (err) {
      console.error('Error fetching games:', err);
    } finally {
      setGamesLoading(false);
    }
  }, [token, gameFilters, gameSearch, gamesCursor]);

  useEffect(() => {
    if (activeTab === 'games' && token) {
      fetchGames(true);
    }
  }, [activeTab, token, gameFilters]);

  // Users Fetch
  const fetchUsers = useCallback(async (reset: boolean = true) => {
    if (!token) return;
    setUsersLoading(true);

    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const params = new URLSearchParams();

      if (userSearch) params.append('search', userSearch);
      if (!reset && usersCursor) params.append('cursor', usersCursor);

      const res = await fetch(`${API_URL}/admin/users?${params}`, { headers });
      if (res.ok) {
        const data = await res.json();
        if (reset) {
          setUsers(data.data.users);
        } else {
          setUsers(prev => [...prev, ...data.data.users]);
        }
        setUsersCursor(data.data.nextCursor);
        setUsersHasMore(data.data.hasMore);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setUsersLoading(false);
    }
  }, [token, userSearch, usersCursor]);

  useEffect(() => {
    if (activeTab === 'users' && token) {
      fetchUsers(true);
    }
  }, [activeTab, token]);

  // Game Detail Fetch
  const fetchGameDetail = async (gameId: string) => {
    if (!token) return;
    setGameDetailLoading(true);
    setShowSensitiveCards(false); // Reset card visibility when selecting new game

    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const res = await fetch(`${API_URL}/admin/games/${gameId}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setSelectedGame(data.data);
      }
    } catch (err) {
      console.error('Error fetching game detail:', err);
    } finally {
      setGameDetailLoading(false);
    }
  };

  // User Detail Fetch
  const fetchUserDetail = async (userId: string) => {
    if (!token) return;
    setUserDetailLoading(true);

    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const res = await fetch(`${API_URL}/admin/users/${userId}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setSelectedUser(data.data);
      }
    } catch (err) {
      console.error('Error fetching user detail:', err);
    } finally {
      setUserDetailLoading(false);
    }
  };

  // Force End Game
  const handleForceEndGame = async (gameId: string) => {
    if (!token) return;
    if (!window.confirm('Are you sure you want to force end this game? This action cannot be undone.')) return;

    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      const res = await fetch(`${API_URL}/admin/games/${gameId}/force-end`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ reason: 'Force ended by admin' })
      });

      if (res.ok) {
        // Refresh game detail and list
        fetchGameDetail(gameId);
        fetchGames(true);
      }
    } catch (err) {
      console.error('Error force ending game:', err);
    }
  };

  // Clear Push Tokens
  const handleClearPushTokens = async (userId: string) => {
    if (!token) return;
    if (!window.confirm('Are you sure you want to clear push tokens for this user? They will need to re-register for notifications.')) return;

    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const res = await fetch(`${API_URL}/admin/users/${userId}/clear-push-tokens`, {
        method: 'POST',
        headers
      });

      if (res.ok) {
        // Refresh user detail
        fetchUserDetail(userId);
      }
    } catch (err) {
      console.error('Error clearing push tokens:', err);
    }
  };

  // Version Config Fetch
  const fetchVersionConfig = useCallback(async () => {
    if (!token) return;
    setSettingsLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const res = await fetch(`${API_URL}/admin/config/version`, { headers });
      if (res.ok) {
        const data = await res.json();
        setVersionConfig(data.data);
      }
    } catch (err) {
      console.error('Error fetching version config:', err);
    } finally {
      setSettingsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (activeTab === 'settings' && token) {
      fetchVersionConfig();
    }
  }, [activeTab, token, fetchVersionConfig]);

  // Save Version Config
  const handleSaveVersionConfig = async () => {
    if (!token) return;
    setSettingsSaving(true);
    setSettingsMessage(null);
    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      const res = await fetch(`${API_URL}/admin/config/version`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          ios: versionConfig.ios,
          android: versionConfig.android,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setVersionConfig(data.data);
        setSettingsMessage({ type: 'success', text: 'Version config saved successfully' });
      } else {
        setSettingsMessage({ type: 'error', text: data.error || 'Failed to save' });
      }
    } catch (err) {
      setSettingsMessage({ type: 'error', text: 'Failed to save version config' });
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchOverviewData();
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/admin');
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString();
  };

  const handleGameSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      fetchGames(true);
    }
  };

  const handleUserSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      fetchUsers(true);
    }
  };

  if (authLoading || loading) {
    return (
      <Container>
        <Main style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
          <LoadingSpinner style={{ fontSize: '2rem', color: 'var(--color-gold)' }} />
        </Main>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Logo>Pasur Admin</Logo>
        <UserInfo>
          <LogoutButton onClick={handleSignOut}>
            <FaSignOutAlt /> Sign Out
          </LogoutButton>
        </UserInfo>
      </Header>

      <TabNav>
        <TabButton $active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
          <FaChartLine /> Overview
        </TabButton>
        <TabButton $active={activeTab === 'games'} onClick={() => setActiveTab('games')}>
          <FaGamepad /> Games
        </TabButton>
        <TabButton $active={activeTab === 'users'} onClick={() => setActiveTab('users')}>
          <FaUsers /> Users
        </TabButton>
        <TabButton $active={activeTab === 'settings'} onClick={() => setActiveTab('settings')}>
          <FaCog /> Settings
        </TabButton>
      </TabNav>

      <Main>
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Stats Section */}
            <Section>
              <SectionTitle>
                <FaUsers /> App Statistics
                <RefreshButton onClick={handleRefresh} disabled={refreshing}>
                  {refreshing ? <LoadingSpinner /> : <FaSync />}
                  Refresh
                </RefreshButton>
              </SectionTitle>
              <StatsGrid>
                <StatCard>
                  <StatLabel>Total Users</StatLabel>
                  <StatValue>{stats?.totalUsers?.toLocaleString() ?? '—'}</StatValue>
                </StatCard>
                <StatCard>
                  <StatLabel>Games (Last 30 Days)</StatLabel>
                  <StatValue>{stats?.recentGames?.toLocaleString() ?? '—'}</StatValue>
                </StatCard>
                <StatCard>
                  <StatLabel>Active Games</StatLabel>
                  <StatValue>{stats?.activeGames?.toLocaleString() ?? '—'}</StatValue>
                </StatCard>
                <StatCard>
                  <StatLabel><FaCalendarDay style={{ marginRight: '0.25rem' }} /> Games Today</StatLabel>
                  <StatValue>{stats?.gamesToday?.toLocaleString() ?? '—'}</StatValue>
                </StatCard>
                <StatCard>
                  <StatLabel><FaCheckCircle style={{ marginRight: '0.25rem' }} /> Completion Rate</StatLabel>
                  <StatValue>{stats?.completionRate != null ? `${stats.completionRate}%` : '—'}</StatValue>
                </StatCard>
              </StatsGrid>

              {/* Game Mode Breakdown */}
              {stats?.gamesByMode && (
                <ChartCard>
                  <ChartTitle>Games by Mode</ChartTitle>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={[
                      { mode: 'Local', count: stats.gamesByMode.LOCAL },
                      { mode: 'Async', count: stats.gamesByMode.ASYNC },
                      { mode: 'Live', count: stats.gamesByMode.LIVE },
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis dataKey="mode" stroke="rgba(255,255,255,0.5)" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }} />
                      <YAxis stroke="rgba(255,255,255,0.5)" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }} />
                      <Tooltip contentStyle={{ backgroundColor: 'rgba(15, 31, 58, 0.95)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: 'white' }} />
                      <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                        <Cell fill="#d4a574" />
                        <Cell fill="#007aff" />
                        <Cell fill="#34a853" />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              )}

              {/* Game Status Breakdown */}
              {stats?.gamesByStatus && (
                <ChartCard>
                  <ChartTitle>Games by Status</ChartTitle>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={[
                      { status: 'Completed', count: stats.gamesByStatus.completed },
                      { status: 'Abandoned', count: stats.gamesByStatus.abandoned },
                      { status: 'Active', count: stats.gamesByStatus.active },
                      { status: 'Waiting', count: stats.gamesByStatus.waiting },
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis dataKey="status" stroke="rgba(255,255,255,0.5)" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }} />
                      <YAxis stroke="rgba(255,255,255,0.5)" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }} />
                      <Tooltip contentStyle={{ backgroundColor: 'rgba(15, 31, 58, 0.95)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: 'white' }} />
                      <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                        <Cell fill="#9c27b0" />
                        <Cell fill="#ff6b6b" />
                        <Cell fill="#34a853" />
                        <Cell fill="#ffc107" />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              )}

              {/* User Growth Chart */}
              {stats?.userGrowth && stats.userGrowth.length > 0 && (
                <ChartCard>
                  <ChartTitle>User Growth (Cumulative)</ChartTitle>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={stats.userGrowth} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis dataKey="month" stroke="rgba(255,255,255,0.5)" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }} />
                      <YAxis stroke="rgba(255,255,255,0.5)" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }} />
                      <Tooltip contentStyle={{ backgroundColor: 'rgba(15, 31, 58, 0.95)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: 'white' }} />
                      <Legend wrapperStyle={{ color: 'rgba(255,255,255,0.8)' }} />
                      <Line type="monotone" dataKey="cumulative" name="Total Users" stroke="#d4a574" strokeWidth={2} dot={{ fill: '#d4a574', r: 3 }} />
                      <Line type="monotone" dataKey="count" name="New Users" stroke="#007aff" strokeWidth={2} dot={{ fill: '#007aff', r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartCard>
              )}
            </Section>

            {/* Revenue Section */}
            <Section>
              <SectionTitle>
                <FaChartLine /> Revenue (Last 30 Days)
              </SectionTitle>
              <RevenueGrid>
                {/* Combined Revenue */}
                <RevenueCard platform="combined">
                  <RevenueHeader>
                    <RevenueIcon platform="combined">
                      <FaChartLine />
                    </RevenueIcon>
                    <RevenuePlatform>Combined Revenue</RevenuePlatform>
                  </RevenueHeader>
                  <RevenueAmount>
                    {revenue ? formatCurrency(revenue.combined.totalRevenue) : '—'}
                  </RevenueAmount>
                  <RevenuePeriod>
                    {revenue ? `${revenue.combined.periodStart} — ${revenue.combined.periodEnd}` : 'Loading...'}
                  </RevenuePeriod>
                </RevenueCard>

                {/* Google Play Revenue */}
                <RevenueCard platform="google">
                  <RevenueHeader>
                    <RevenueIcon platform="google">
                      <FaGooglePlay />
                    </RevenueIcon>
                    <RevenuePlatform>Google Play</RevenuePlatform>
                  </RevenueHeader>
                  <RevenueAmount>
                    {revenue ? formatCurrency(revenue.googlePlay.totalRevenue) : '—'}
                  </RevenueAmount>
                  {revenue?.googlePlay?.error && (
                    <ErrorBanner>
                      <FaExclamationTriangle />
                      {revenue.googlePlay.error}
                    </ErrorBanner>
                  )}
                </RevenueCard>

                {/* App Store Revenue */}
                <RevenueCard platform="apple">
                  <RevenueHeader>
                    <RevenueIcon platform="apple">
                      <FaApple />
                    </RevenueIcon>
                    <RevenuePlatform>App Store</RevenuePlatform>
                  </RevenueHeader>
                  <RevenueAmount>
                    {revenue ? formatCurrency(revenue.appStore.totalRevenue) : '—'}
                  </RevenueAmount>
                  {revenue?.appStore?.error && (
                    <ErrorBanner>
                      <FaExclamationTriangle />
                      {revenue.appStore.error}
                    </ErrorBanner>
                  )}
                </RevenueCard>
              </RevenueGrid>

              {/* Revenue Trend Chart */}
              <ChartCard>
                <ChartTitle>Daily Revenue Trend</ChartTitle>
                {revenue?.appStore?.dailyRevenue && revenue.appStore.dailyRevenue.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart
                      data={revenue.appStore.dailyRevenue.map(item => ({
                        date: item.date.split('-').slice(1).join('/'), // Format as MM/DD
                        'App Store': item.revenue,
                        'Google Play': revenue.googlePlay.dailyRevenue?.find(g => g.date === item.date)?.revenue || 0
                      }))}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis
                        dataKey="date"
                        stroke="rgba(255,255,255,0.5)"
                        tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }}
                      />
                      <YAxis
                        stroke="rgba(255,255,255,0.5)"
                        tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }}
                        tickFormatter={(value) => `$${value}`}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'rgba(15, 31, 58, 0.95)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: '8px',
                          color: 'white'
                        }}
                        formatter={(value) => [`$${(value as number || 0).toFixed(2)}`, undefined]}
                      />
                      <Legend
                        wrapperStyle={{ color: 'rgba(255,255,255,0.8)' }}
                      />
                      <Line
                        type="monotone"
                        dataKey="App Store"
                        stroke="#007aff"
                        strokeWidth={2}
                        dot={{ fill: '#007aff', strokeWidth: 2, r: 3 }}
                        activeDot={{ r: 5 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="Google Play"
                        stroke="#34a853"
                        strokeWidth={2}
                        dot={{ fill: '#34a853', strokeWidth: 2, r: 3 }}
                        activeDot={{ r: 5 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <NoDataMessage>
                    No daily revenue data available. Reports typically have a 1-2 day delay.
                  </NoDataMessage>
                )}
              </ChartCard>
            </Section>
          </>
        )}

        {/* Games Tab */}
        {activeTab === 'games' && (
          <Section>
            <SectionTitle>
              <FaGamepad /> Game Management
            </SectionTitle>

            <FilterBar>
              <FilterSelect
                value={gameFilters.status}
                onChange={(e) => setGameFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="all">All Status</option>
                <option value="WAITING">Waiting</option>
                <option value="ACTIVE">Active</option>
                <option value="PAUSED">Paused</option>
                <option value="COMPLETED">Completed</option>
                <option value="ABANDONED">Abandoned</option>
              </FilterSelect>

              <FilterSelect
                value={gameFilters.mode}
                onChange={(e) => setGameFilters(prev => ({ ...prev, mode: e.target.value }))}
              >
                <option value="all">All Modes</option>
                <option value="LOCAL">Local</option>
                <option value="ASYNC">Async</option>
                <option value="LIVE">Live</option>
              </FilterSelect>

              <SearchInput>
                <FaSearch style={{ color: 'rgba(255,255,255,0.4)' }} />
                <input
                  type="text"
                  placeholder="Search by Game ID..."
                  value={gameSearch}
                  onChange={(e) => setGameSearch(e.target.value)}
                  onKeyDown={handleGameSearch}
                />
                {gameSearch && (
                  <CloseButton onClick={() => { setGameSearch(''); fetchGames(true); }} style={{ padding: '0.25rem' }}>
                    <FaTimes size={12} />
                  </CloseButton>
                )}
              </SearchInput>
            </FilterBar>

            {gamesLoading && games.length === 0 ? (
              <NoDataMessage><LoadingSpinner /> Loading games...</NoDataMessage>
            ) : games.length === 0 ? (
              <NoDataMessage>No games found</NoDataMessage>
            ) : (
              <>
                <Table>
                  <thead>
                    <tr>
                      <Th>ID</Th>
                      <Th>Status</Th>
                      <Th>Mode</Th>
                      <Th>Players</Th>
                      <Th>Round</Th>
                      <Th>Created</Th>
                      <Th>Last Activity</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {games.map(game => (
                      <Tr key={game.id} onClick={() => fetchGameDetail(game.id)}>
                        <Td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{game.id.slice(0, 8)}...</Td>
                        <Td><StatusBadge status={game.status}>{game.status}</StatusBadge></Td>
                        <Td>{game.mode}</Td>
                        <Td>{game.players.map((p, i) => (
                          <span key={p.id}>
                            {i > 0 && ', '}
                            {p.isBot ? (
                              <>🤖</>
                            ) : (
                              <GameLink onClick={(e) => { e.stopPropagation(); fetchUserDetail(p.id); }}>{p.name}</GameLink>
                            )}
                          </span>
                        ))}</Td>
                        <Td>{game.currentRound}</Td>
                        <Td>{formatDate(game.createdAt)}</Td>
                        <Td>{formatDate(game.lastActivityAt)}</Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>

                {gamesHasMore && (
                  <LoadMoreButton onClick={() => fetchGames(false)} disabled={gamesLoading}>
                    {gamesLoading ? <LoadingSpinner /> : 'Load More'}
                  </LoadMoreButton>
                )}
              </>
            )}
          </Section>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <Section>
            <SectionTitle>
              <FaUsers /> User Management
            </SectionTitle>

            <FilterBar>
              <SearchInput>
                <FaSearch style={{ color: 'rgba(255,255,255,0.4)' }} />
                <input
                  type="text"
                  placeholder="Search by User ID, email, or name..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  onKeyDown={handleUserSearch}
                />
                {userSearch && (
                  <CloseButton onClick={() => { setUserSearch(''); fetchUsers(true); }} style={{ padding: '0.25rem' }}>
                    <FaTimes size={12} />
                  </CloseButton>
                )}
              </SearchInput>
            </FilterBar>

            {usersLoading && users.length === 0 ? (
              <NoDataMessage><LoadingSpinner /> Loading users...</NoDataMessage>
            ) : users.length === 0 ? (
              <NoDataMessage>No users found</NoDataMessage>
            ) : (
              <>
                <Table>
                  <thead>
                    <tr>
                      <Th>ID</Th>
                      <Th>Name</Th>
                      <Th>Email</Th>
                      <Th>Games Played</Th>
                      <Th>Win Rate</Th>
                      <Th>Rating</Th>
                      <Th>Active Games</Th>
                      <Th>Last Active</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(user => (
                      <Tr key={user.id} onClick={() => fetchUserDetail(user.id)}>
                        <Td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{user.id.slice(0, 8)}...</Td>
                        <Td>{user.displayName || '—'}</Td>
                        <Td>{user.email || '—'}</Td>
                        <Td>{user.stats?.gamesPlayed ?? '—'}</Td>
                        <Td>
                          {user.stats?.gamesPlayed && user.stats.gamesPlayed > 0
                            ? `${((user.stats.gamesWon / user.stats.gamesPlayed) * 100).toFixed(1)}%`
                            : '—'}
                        </Td>
                        <Td>{user.stats?.rating ?? '—'}</Td>
                        <Td>{user.activeGamesCount}</Td>
                        <Td>{formatDate(user.lastActiveAt || '')}</Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>

                {usersHasMore && (
                  <LoadMoreButton onClick={() => fetchUsers(false)} disabled={usersLoading}>
                    {usersLoading ? <LoadingSpinner /> : 'Load More'}
                  </LoadMoreButton>
                )}
              </>
            )}
          </Section>
        )}
        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <Section>
            <SectionTitle>
              <FaMobileAlt /> App Version Requirements
            </SectionTitle>

            {settingsLoading ? (
              <NoDataMessage><LoadingSpinner /> Loading settings...</NoDataMessage>
            ) : (
              <div style={{ maxWidth: 600 }}>
                {settingsMessage && (
                  <ErrorBanner style={{
                    marginBottom: '1rem',
                    background: settingsMessage.type === 'success' ? 'rgba(52, 168, 83, 0.15)' : undefined,
                    borderColor: settingsMessage.type === 'success' ? 'rgba(52, 168, 83, 0.3)' : undefined,
                    color: settingsMessage.type === 'success' ? '#34a853' : undefined,
                  }}>
                    {settingsMessage.type === 'success' ? <FaCheckCircle /> : <FaExclamationTriangle />}
                    {settingsMessage.text}
                  </ErrorBanner>
                )}

                <ChartCard>
                  <ChartTitle><FaApple style={{ marginRight: '0.5rem' }} /> iOS</ChartTitle>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <StatLabel>Min Version (force update below this)</StatLabel>
                      <SearchInput style={{ maxWidth: 'none', marginTop: '0.25rem' }}>
                        <input
                          type="text"
                          placeholder="e.g. 1.0.100"
                          value={versionConfig.ios.minVersion}
                          onChange={(e) => setVersionConfig(prev => ({
                            ...prev,
                            ios: { ...prev.ios, minVersion: e.target.value }
                          }))}
                        />
                      </SearchInput>
                    </div>
                    <div>
                      <StatLabel>Latest Version (soft prompt below this)</StatLabel>
                      <SearchInput style={{ maxWidth: 'none', marginTop: '0.25rem' }}>
                        <input
                          type="text"
                          placeholder="e.g. 1.0.115"
                          value={versionConfig.ios.latestVersion}
                          onChange={(e) => setVersionConfig(prev => ({
                            ...prev,
                            ios: { ...prev.ios, latestVersion: e.target.value }
                          }))}
                        />
                      </SearchInput>
                    </div>
                  </div>
                </ChartCard>

                <ChartCard>
                  <ChartTitle><FaGooglePlay style={{ marginRight: '0.5rem' }} /> Android</ChartTitle>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <StatLabel>Min Version (force update below this)</StatLabel>
                      <SearchInput style={{ maxWidth: 'none', marginTop: '0.25rem' }}>
                        <input
                          type="text"
                          placeholder="e.g. 1.0.100"
                          value={versionConfig.android.minVersion}
                          onChange={(e) => setVersionConfig(prev => ({
                            ...prev,
                            android: { ...prev.android, minVersion: e.target.value }
                          }))}
                        />
                      </SearchInput>
                    </div>
                    <div>
                      <StatLabel>Latest Version (soft prompt below this)</StatLabel>
                      <SearchInput style={{ maxWidth: 'none', marginTop: '0.25rem' }}>
                        <input
                          type="text"
                          placeholder="e.g. 1.0.115"
                          value={versionConfig.android.latestVersion}
                          onChange={(e) => setVersionConfig(prev => ({
                            ...prev,
                            android: { ...prev.android, latestVersion: e.target.value }
                          }))}
                        />
                      </SearchInput>
                    </div>
                  </div>
                </ChartCard>

                {versionConfig.updatedAt && (
                  <div style={{ marginTop: '1rem', fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)' }}>
                    Last updated: {formatDate(versionConfig.updatedAt)}
                  </div>
                )}

                <ActionRow style={{ borderTop: 'none', paddingTop: '0.5rem' }}>
                  <ActionButton onClick={handleSaveVersionConfig} disabled={settingsSaving}>
                    {settingsSaving ? <LoadingSpinner /> : <FaSave />}
                    {settingsSaving ? 'Saving...' : 'Save Version Config'}
                  </ActionButton>
                </ActionRow>

                <DebugSection style={{ marginTop: '1.5rem' }}>
                  <DebugTitle>How it works</DebugTitle>
                  <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)', lineHeight: '1.6' }}>
                    <strong style={{ color: 'rgba(255,255,255,0.8)' }}>Min Version:</strong> Users below this version see a non-dismissible "Update Required" screen. Set this when deploying breaking backend changes.<br />
                    <strong style={{ color: 'rgba(255,255,255,0.8)' }}>Latest Version:</strong> Users below this (but above min) see a dismissible "Update Available" prompt. Set to the current App Store / Play Store version.<br /><br />
                    Changes take effect within 1 minute (cached). The version check runs once per app launch (throttled to every 6 hours per device).
                  </div>
                </DebugSection>
              </div>
            )}
          </Section>
        )}
      </Main>

      {/* Game Detail Modal */}
      {selectedGame && (
        <ModalOverlay onClick={() => setSelectedGame(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Game Details</ModalTitle>
              <CloseButton onClick={() => setSelectedGame(null)}>
                <FaTimes />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              {gameDetailLoading ? (
                <NoDataMessage><LoadingSpinner /> Loading...</NoDataMessage>
              ) : (
                <>
                  <DetailRow>
                    <DetailLabel>Game ID</DetailLabel>
                    <DetailValue style={{ fontFamily: 'monospace' }}>{selectedGame.id}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Status</DetailLabel>
                    <DetailValue><StatusBadge status={selectedGame.status}>{selectedGame.status}</StatusBadge></DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Mode</DetailLabel>
                    <DetailValue>{selectedGame.mode}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Current Round</DetailLabel>
                    <DetailValue>{selectedGame.currentRound}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Created</DetailLabel>
                    <DetailValue>{formatDate(selectedGame.createdAt)}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Last Activity</DetailLabel>
                    <DetailValue>{formatDate(selectedGame.lastActivityAt)}</DetailValue>
                  </DetailRow>
                  {selectedGame.inviteCode && (
                    <DetailRow>
                      <DetailLabel>Invite Code</DetailLabel>
                      <DetailValue style={{ fontFamily: 'monospace' }}>{selectedGame.inviteCode}</DetailValue>
                    </DetailRow>
                  )}
                  {selectedGame.winner && (
                    <DetailRow>
                      <DetailLabel>Winner</DetailLabel>
                      <DetailValue>{selectedGame.winner}</DetailValue>
                    </DetailRow>
                  )}

                  <DebugTitle style={{ marginTop: '1.5rem' }}>Players</DebugTitle>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Name</Th>
                        <Th>Score</Th>
                        <Th>Bot</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedGame.players.map(player => (
                        <tr key={player.id}>
                          <Td>{player.isBot ? '🤖' : player.name}</Td>
                          <Td>{(selectedGame as any).scores?.[player.id]?.total ?? player.score}</Td>
                          <Td>{player.isBot ? 'Yes' : 'No'}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  {/* Round-by-Round Scores */}
                  {(selectedGame as any).scores && (() => {
                    const scores = (selectedGame as any).scores;
                    const maxRounds = Math.max(
                      ...selectedGame.players.map(p => scores[p.id]?.roundPoints?.length || 0)
                    );
                    if (maxRounds === 0) return null;
                    return (
                      <>
                        <DebugTitle style={{ marginTop: '1.5rem' }}>Round History</DebugTitle>
                        <Table>
                          <thead>
                            <tr>
                              <Th>Round</Th>
                              {selectedGame.players.map(p => (
                                <Th key={p.id}>{p.isBot ? '🤖' : p.name}</Th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {Array.from({ length: maxRounds }, (_, i) => (
                              <tr key={i}>
                                <Td>{i + 1}</Td>
                                {selectedGame.players.map(p => {
                                  const pts = scores[p.id]?.roundPoints?.[i] ?? '-';
                                  const surs = scores[p.id]?.roundSurs?.[i];
                                  return (
                                    <Td key={p.id}>
                                      {pts}{surs ? ` (${surs} sur${surs > 1 ? 's' : ''})` : ''}
                                    </Td>
                                  );
                                })}
                              </tr>
                            ))}
                            <tr style={{ fontWeight: 'bold' }}>
                              <Td>Total</Td>
                              {selectedGame.players.map(p => (
                                <Td key={p.id}>{scores[p.id]?.total ?? 0}</Td>
                              ))}
                            </tr>
                          </tbody>
                        </Table>
                      </>
                    );
                  })()}

                  {selectedGame._debug && (
                    <DebugSection>
                      <DebugTitle>Debug Info (Admin Only)</DebugTitle>
                      <DetailRow>
                        <DetailLabel>Cards in Deck</DetailLabel>
                        <DetailValue>{selectedGame._debug.deckCount}</DetailValue>
                      </DetailRow>
                      <DetailRow>
                        <DetailLabel>Table Cards</DetailLabel>
                        <DetailValue>{selectedGame.tableCards?.length || 0}</DetailValue>
                      </DetailRow>

                      {/* Table Cards Visual */}
                      {selectedGame.tableCards && selectedGame.tableCards.length > 0 && (
                        <CardRow>
                          {selectedGame.tableCards.map((card: { suit: string; rank: number }) =>
                            renderCard(card)
                          )}
                        </CardRow>
                      )}

                      {/* Player Hands Section */}
                      <PlayerHandSection>
                        <DetailRow>
                          <DetailLabel>Player Hands</DetailLabel>
                          <RevealButton onClick={() => setShowSensitiveCards(!showSensitiveCards)}>
                            <FaEye /> {showSensitiveCards ? 'Hide Cards' : 'Reveal Cards'}
                          </RevealButton>
                        </DetailRow>

                        {selectedGame._debug.playerHands?.map((playerHand: { playerId: string; hand: any[] }) => {
                          const player = selectedGame.players.find(p => p.id === playerHand.playerId);
                          return (
                            <div key={playerHand.playerId} style={{ marginTop: '0.75rem' }}>
                              <PlayerHandLabel>
                                {player?.isBot ? '🤖' : (player?.name || playerHand.playerId)} ({playerHand.hand?.length || 0} cards)
                              </PlayerHandLabel>
                              <CardRow>
                                {showSensitiveCards
                                  ? playerHand.hand?.map((card: { suit: string; rank: number }) =>
                                      renderCard(card)
                                    )
                                  : playerHand.hand?.map((_: any, i: number) =>
                                      renderHiddenCard(i)
                                    )
                                }
                              </CardRow>
                            </div>
                          );
                        })}
                      </PlayerHandSection>
                    </DebugSection>
                  )}

                  {selectedGame.status !== 'COMPLETED' && selectedGame.status !== 'ABANDONED' && (
                    <ActionRow>
                      <ActionButton
                        variant="danger"
                        onClick={() => handleForceEndGame(selectedGame.id)}
                      >
                        <FaStopCircle /> Force End Game
                      </ActionButton>
                    </ActionRow>
                  )}
                </>
              )}
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* User Detail Modal */}
      {selectedUser && (
        <ModalOverlay onClick={() => setSelectedUser(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>User Details</ModalTitle>
              <CloseButton onClick={() => setSelectedUser(null)}>
                <FaTimes />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              {userDetailLoading ? (
                <NoDataMessage><LoadingSpinner /> Loading...</NoDataMessage>
              ) : (
                <>
                  <DetailRow>
                    <DetailLabel>User ID</DetailLabel>
                    <DetailValue style={{ fontFamily: 'monospace' }}>{selectedUser.id}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Display Name</DetailLabel>
                    <DetailValue>{selectedUser.displayName || '—'}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Email</DetailLabel>
                    <DetailValue>{selectedUser.email || '—'}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Email Verified</DetailLabel>
                    <DetailValue>{selectedUser.emailVerified ? 'Yes' : 'No'}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Account Disabled</DetailLabel>
                    <DetailValue>{selectedUser.disabled ? 'Yes' : 'No'}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Created</DetailLabel>
                    <DetailValue>{formatDate(selectedUser.createdAt || '')}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Last Sign In</DetailLabel>
                    <DetailValue>{formatDate(selectedUser.lastSignInAt || '')}</DetailValue>
                  </DetailRow>

                  {selectedUser.stats && (
                    <>
                      <DebugTitle style={{ marginTop: '1.5rem' }}>Stats</DebugTitle>
                      <DetailRow>
                        <DetailLabel>Games Played</DetailLabel>
                        <DetailValue>{selectedUser.stats.gamesPlayed}</DetailValue>
                      </DetailRow>
                      <DetailRow>
                        <DetailLabel>Games Won</DetailLabel>
                        <DetailValue>{selectedUser.stats.gamesWon}</DetailValue>
                      </DetailRow>
                      <DetailRow>
                        <DetailLabel>Win Rate</DetailLabel>
                        <DetailValue>
                          {selectedUser.stats.gamesPlayed > 0
                            ? `${((selectedUser.stats.gamesWon / selectedUser.stats.gamesPlayed) * 100).toFixed(1)}%`
                            : '—'}
                        </DetailValue>
                      </DetailRow>
                      <DetailRow>
                        <DetailLabel>Rating</DetailLabel>
                        <DetailValue>{selectedUser.stats.rating}</DetailValue>
                      </DetailRow>
                    </>
                  )}

                  <DebugTitle style={{ marginTop: '1.5rem' }}>Push Notifications</DebugTitle>
                  <DetailRow>
                    <DetailLabel>Push Tokens</DetailLabel>
                    <DetailValue>{selectedUser.pushTokenCount || 0} registered</DetailValue>
                  </DetailRow>
                  {selectedUser.pushTokens && selectedUser.pushTokens.length > 0 && (
                    <DebugSection>
                      <DebugCode>
                        {selectedUser.pushTokens.map((token, i) => (
                          <div key={i}>{token}</div>
                        ))}
                      </DebugCode>
                    </DebugSection>
                  )}

                  {selectedUser.subscription && (
                    <>
                      <DebugTitle style={{ marginTop: '1.5rem' }}>Subscription</DebugTitle>
                      <DebugSection>
                        <DebugCode>
                          {JSON.stringify(selectedUser.subscription, null, 2)}
                        </DebugCode>
                      </DebugSection>
                    </>
                  )}

                  {selectedUser.recentGames && selectedUser.recentGames.length > 0 && (
                    <>
                      <DebugTitle style={{ marginTop: '1.5rem' }}>Recent Games</DebugTitle>
                      <Table>
                        <thead>
                          <tr>
                            <Th>Game ID</Th>
                            <Th>Status</Th>
                            <Th>Players</Th>
                            <Th>Date</Th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedUser.recentGames.map(game => (
                            <Tr key={game.id} onClick={() => { setSelectedUser(null); fetchGameDetail(game.id); }}>
                              <Td>
                                <GameLink onClick={(e) => { e.stopPropagation(); setSelectedUser(null); fetchGameDetail(game.id); }}>
                                  {game.id.slice(0, 8)}...
                                </GameLink>
                              </Td>
                              <Td><StatusBadge status={game.status}>{game.status}</StatusBadge></Td>
                              <Td>{game.players.map(p => p.isBot ? '🤖' : p.name).join(', ')}</Td>
                              <Td>{formatDate(game.lastActivityAt)}</Td>
                            </Tr>
                          ))}
                        </tbody>
                      </Table>
                    </>
                  )}

                  <ActionRow>
                    <ActionButton
                      variant="warning"
                      onClick={() => handleClearPushTokens(selectedUser.id)}
                    >
                      <FaTrash /> Clear Push Tokens
                    </ActionButton>
                  </ActionRow>
                </>
              )}
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default AdminDashboardPage;
